import classNames from "classnames";
import { stringify } from "csv-stringify/lib";
import { useContext, useState } from "react";
import { useHistory, useParams } from "react-router";
import {
	common_utils,
	RemoveVoid,
} from "../../../../../../../../common/common_utils";
import { Constants } from "../../../../../../../../common/constants";
import {
	CSVWebResult,
	DiscordAuthToken,
	GetListingsRequest,
} from "../../../../../../../../common/sesh_types";
import { RouterInferenceHelpers } from "../../../../../../../../src/api/trpc/rootRouter";
import { DashboardUtils } from "../../../../../dashboard_utils";
import { GuildListContext } from "../../../../../sesh_router";
import { trpc } from "../../../../../utils/trpc";
import { RemovingThrobber } from "../../../../removing_throbber";
import { LoadingThrobber } from "../../../base_components/loading_throbber";
import { SeshPrimaryButton } from "../../../base_components/sesh_primary_button";
import { SeshSearchForm } from "../../../base_components/sesh_search_form";
import { CSVDownload } from "../../../csv_download";
import { useEventListQuery } from "../../../hooks/use_event_list_query";
import { useUserContext } from "../../../hooks/use_user_context";
import { PageButtons } from "../../page_buttons";
import EventRow from "./event_row";

type EventSortType = RemoveVoid<
	RouterInferenceHelpers["event"]["list"]["input"]
>["sort"];

async function downloadEventListingsCSV(
	auth_token: DiscordAuthToken,
	guild_id: string
) {
	let request_body: GetListingsRequest = {
		access_token: auth_token.access_token,
		token_type: auth_token.token_type,
		guild_id: guild_id,
	};

	return fetch(`/api/get_event_listings_csv`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(request_body),
	}).then((response) =>
		response.json().then((value: CSVWebResult) => {
			if (value.err) {
				DashboardUtils.ShowDefaultErrorAlert(value.err);
				return;
			}

			stringify(value.result, (err, output) => {
				if (err) {
					DashboardUtils.ShowDefaultErrorAlert(err.toString());
					return;
				}

				const element = document.createElement("a");

				const file = new Blob([output], { type: "text/plain" });
				element.href = URL.createObjectURL(file);
				element.download = `events_${guild_id}.csv`;
				document.body.appendChild(element); // Required for this to work in FireFox
				element.click();
			});

			return value.result;
		})
	);
}

/**
 * NOTE: This is pasted from EventListingForm/PollListingForm. Ideally we should extract out shared components but its fine for now
 */
export default function EventListingForm(props: { viewToggler: JSX.Element }) {
	const { server_id } = useParams<{ server_id: string }>();

	const guild_list = useContext(GuildListContext);
	const history = useHistory();

	const user_context = useUserContext();

	const [page, setPage] = useState(0);
	const [sort, setSort] = useState<EventSortType>({
		type: "Date",
		direction: "ASC",
	});
	const [include_completed, setIncludeCompleted] = useState(false);
	const [search, setSearch] = useState("");
	const [is_csv_loading, setIsCSVLoading] = useState(false);

	const list_query = useEventListQuery({
		guild_id: server_id,
		include_completed,
		cursor: page,
		sort,
		search,
	});

	if (!list_query.data) {
		return <LoadingThrobber />;
	}

	const query_items = list_query.data?.items;

	const disabled = list_query.isPreviousData;

	return (
		<div
			className={classNames("flex flex-col gap-2", {
				"input-disabled": disabled,
			})}
		>
			{props.viewToggler}
			<div className="mt-8 flex flex-col items-center">
				<div className="sm:w-3/4">
					<div className="flex flex-col items-center gap-4 sm:flex-row">
						<SeshSearchForm
							onSubmit={setSearch}
							placeholder="Search by name"
						/>
						<div
							className={"cursor-pointer"}
							onClick={
								disabled
									? undefined
									: () =>
											setIncludeCompleted(
												!include_completed
											)
							}
						>
							<input
								type="checkbox"
								className="cursor-pointer"
								checked={include_completed}
								onChange={() => {}}
							/>{" "}
							Include completed events
						</div>
						<CSVDownload
							is_loading={is_csv_loading}
							onClick={() => {
								setIsCSVLoading(true);
								downloadEventListingsCSV(
									user_context,
									server_id
								).finally(() => setIsCSVLoading(false));
							}}
						/>
					</div>
					<table
						className={classNames(
							"sesh-table listing-table responsive-table event-listings"
						)}
					>
						<thead>
							<tr>
								<th className="w-[27%]">
									<SortableTableHeader
										type="Date"
										current_sort={sort}
										setSort={setSort}
										is_loading={list_query.isPreviousData}
									/>
								</th>
								<th className="w-[23%]">
									<SortableTableHeader
										type="Name"
										current_sort={sort}
										setSort={setSort}
										is_loading={list_query.isPreviousData}
									/>
								</th>
								<th className="w-[12%]">
									<SortableTableHeader
										type="Channel"
										current_sort={sort}
										setSort={setSort}
										is_loading={list_query.isPreviousData}
									/>
								</th>
								<th className="w-[12%]">
									<SortableTableHeader
										type="Author"
										current_sort={sort}
										setSort={setSort}
										is_loading={list_query.isPreviousData}
									/>
								</th>
								<th className="w-[15%]">
									<SortableTableHeader
										type="RSVPs"
										current_sort={sort}
										setSort={setSort}
										is_loading={list_query.isPreviousData}
									/>
								</th>
								<th className="w-[11%] overflow-hidden overflow-ellipsis">
									Actions
								</th>
							</tr>
						</thead>
						<tbody>
							{query_items && query_items.length > 0 ? (
								query_items.map((val) => {
									return (
										<EventRow
											key={val.message_id}
											message_id={val.message_id}
										/>
									);
								})
							) : (
								<tr className="">
									<td colSpan={6} className="text-center">
										No results found
									</td>
								</tr>
							)}
						</tbody>
					</table>
					<div className="flex w-full flex-wrap-reverse gap-2">
						<div className="w-fit">
							<SeshPrimaryButton
								onClick={() =>
									history.push(
										common_utils.getEventCreateURL(
											server_id
										)
									)
								}
							>
								<div className="flex items-center gap-2 px-2">
									<i className="fas fa-plus-circle"></i>
									<div>New Event</div>
								</div>
							</SeshPrimaryButton>
						</div>
						<div className="ml-auto">
							{list_query.data && (
								<PageButtons
									page={page}
									total_items={
										list_query.data?.total_item_count ?? 0
									}
									setPage={setPage}
									is_loading={list_query.isPreviousData}
									items_per_page={
										Constants.LISTING_PAGINATION_LIMIT
									}
								/>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

const OppositeSortMap = {
	ASC: "DESC",
	DESC: "ASC",
} as const;

function SortableTableHeader(props: {
	type: EventSortType["type"];
	current_sort: EventSortType;
	setSort: (type: EventSortType) => void;
	is_loading?: boolean;
}) {
	const is_current_sort = props.current_sort.type === props.type;

	const is_pending_results = props.is_loading && is_current_sort;

	return (
		<div
			className={classNames("group flex flex-wrap items-center gap-1", {
				"cursor-pointer": !props.is_loading,
			})}
			onClick={() => {
				if (props.is_loading) {
					return;
				}
				props.setSort({
					type: props.type,
					direction: is_current_sort
						? OppositeSortMap[props.current_sort.direction]
						: "ASC",
				});
			}}
		>
			<div className="max-w-full overflow-hidden overflow-ellipsis">
				{props.type}
			</div>
			<span
				key={`${props.type}_${props.current_sort.direction}_${is_current_sort}_${is_pending_results}`}
			>
				<i
					className={classNames("group-hover:text-sesh-primary", {
						hidden: is_pending_results,
						"fas fa-sort text-base-light": !is_current_sort,
						"fas fa-sort-down":
							is_current_sort &&
							props.current_sort.direction === "DESC",
						"fas fa-sort-up":
							is_current_sort &&
							props.current_sort.direction === "ASC",
					})}
				/>
				{is_pending_results && <RemovingThrobber />}
			</span>
		</div>
	);
}
